import { CSSProperties, useEffect, useRef, useState } from "react";
import { IReservation, ITableAvailability } from "../models/IReservation";
import { confirmReservation, getTableAvailability } from "../services/reservationService";
import { isBelowMinCapacity, isTableSelectable } from "../utils/tableAvailability";

interface Props {
  reservation: IReservation;
  onClose: () => void;
  onConfirmed: () => void;
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function defaultDuration(reservation: IReservation): number {
  const diffMs = new Date(reservation.endsAtUtc).getTime() - new Date(reservation.startsAtUtc).getTime();
  const minutes = Math.round(diffMs / 60000);
  return minutes > 0 ? minutes : 120;
}

function reasonLabel(t: ITableAvailability): string {
  switch (t.unavailableReason) {
    case "RestaurantClosed":
      return "Restoran bu saatte kapalı";
    case "Booked": {
      const b = t.bookings?.[0];
      return b ? `${formatTime(b.startsAtUtc)}–${formatTime(b.endsAtUtc)} dolu` : "Dolu";
    }
    case "TooSmall":
      return `En fazla ${t.capacity} kişi`;
    case "TooLarge":
      return `En az ${t.minCapacity} kişilik`;
    default:
      return "Uygun değil";
  }
}

const inputStyle: CSSProperties = { borderRadius: 10, border: "1.5px solid #e5e7eb" };

export default function ConfirmReservationModal({ reservation, onClose, onConfirmed }: Props) {
  const [tableId, setTableId] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(String(defaultDuration(reservation)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [availability, setAvailability] = useState<ITableAvailability[] | null>(null);
  const [availLoading, setAvailLoading] = useState(true);
  const [availError, setAvailError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchAvailability = () => {
    setAvailLoading(true);
    setAvailError("");
    const duration = Number(durationMinutes);
    getTableAvailability(reservation.restourantId, reservation.startsAtUtc, reservation.partySize, duration > 0 ? duration : undefined)
      .then((res) => {
        setAvailability(res.data ?? []);
        // Seçili masa artık uygun değilse seçimi temizle
        setTableId((prev) => {
          const stillAvailable = (res.data ?? []).some((t) => t.id === prev && isTableSelectable(t));
          return stillAvailable ? prev : "";
        });
      })
      .catch(() => setAvailError("Masa uygunluğu alınamadı."))
      .finally(() => setAvailLoading(false));
  };

  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDurationChange = (value: string) => {
    setDurationMinutes(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchAvailability, 500);
  };

  const allClosed = !!availability && availability.length > 0 && availability.every((t) => t.unavailableReason === "RestaurantClosed");
  const selectedTable = availability?.find((t) => t.id === tableId);

  const handleConfirm = () => {
    if (!tableId) {
      setError("Lütfen müsait bir masa seçin.");
      return;
    }
    if (!durationMinutes || Number(durationMinutes) <= 0) {
      setError("Lütfen geçerli bir süre girin.");
      return;
    }
    setSaving(true);
    setError("");
    confirmReservation(reservation.id, tableId, Number(durationMinutes))
      .then(() => {
        onConfirmed();
        onClose();
      })
      .catch((err) => {
        const code = err?.response?.data?.[0]?.code;
        if (code === "TableAlreadyBooked") {
          setError("Bu masa az önce başka bir rezervasyon tarafından alındı. Liste güncellendi, lütfen başka bir masa seçin.");
          fetchAvailability();
        } else {
          setError("Rezervasyon onaylanamadı.");
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 20, padding: "28px 28px 22px", width: "min(96vw, 440px)", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-check2-circle" style={{ color: "#0d9488", fontSize: "1.05rem" }}></i>
            </div>
            Rezervasyonu Onayla
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.2rem" }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Rezervasyon özeti */}
        <div style={{ background: "#f9fafb", borderRadius: 12, padding: "12px 14px", marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827" }}>{reservation.contactName}</div>
          <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}>
            <i className="bi bi-telephone-fill me-1"></i>{reservation.contactPhone}
            {" · "}
            <i className="bi bi-people-fill me-1"></i>{reservation.partySize} kişi
          </div>
          <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}>
            <i className="bi bi-calendar-event me-1"></i>{formatDateTime(reservation.startsAtUtc)}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Süre (dakika)</label>
            <input
              type="number"
              value={durationMinutes}
              onChange={(e) => handleDurationChange(e.target.value)}
              className="form-control"
              style={inputStyle}
              placeholder="Örn. 120"
            />
          </div>

          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span>Masa Seç</span>
              {availLoading && <span className="spinner-border spinner-border-sm text-secondary" style={{ width: 13, height: 13 }} />}
            </label>

            {availError && (
              <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.8rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span><i className="bi bi-exclamation-circle me-1"></i>{availError}</span>
                <button onClick={fetchAvailability} style={{ background: "none", border: "none", color: "#b91c1c", fontWeight: 700, cursor: "pointer", fontSize: "0.78rem", flexShrink: 0 }}>Tekrar Dene</button>
              </div>
            )}

            {allClosed && (
              <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "8px 12px", color: "#92400e", fontSize: "0.8rem", marginBottom: 8 }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>Restoran bu saatte kapalı — hiçbir masa uygun değil.
              </div>
            )}

            {availability && !availLoading && (
              <div style={{ display: "flex", flexDirection: "column", gap: 7, maxHeight: 260, overflowY: "auto", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: 8 }}>
                {availability.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af", fontSize: "0.82rem" }}>
                    Restorana ait masa bulunamadı.
                  </div>
                ) : (
                  availability.map((t) => {
                    const selected = tableId === t.id;
                    const disabled = !isTableSelectable(t);
                    const belowMin = isBelowMinCapacity(t);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => setTableId(t.id)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                          padding: "9px 12px", borderRadius: 9, textAlign: "left",
                          border: `1.5px solid ${selected ? "#0d9488" : disabled ? "#f1f5f9" : "#e5e7eb"}`,
                          background: selected ? "#f0fdfa" : disabled ? "#f9fafb" : "#fff",
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.65 : 1,
                          transition: "border-color 0.15s, background 0.15s",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: "0.85rem", color: disabled ? "#9ca3af" : "#111827" }}>
                            Masa {t.tableNumber}{t.label ? ` — ${t.label}` : ""}
                          </div>
                          <div style={{ fontSize: "0.74rem", color: "#9ca3af" }}>{t.minCapacity}–{t.capacity} kişi</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          {(disabled || belowMin) && (
                            <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#b45309", background: "#fffbeb", padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>
                              {belowMin && <i className="bi bi-exclamation-triangle me-1"></i>}
                              {reasonLabel(t)}
                            </span>
                          )}
                          {selected && <i className="bi bi-check-circle-fill" style={{ color: "#0d9488" }}></i>}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {selectedTable && isBelowMinCapacity(selectedTable) && (
              <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "8px 12px", color: "#92400e", fontSize: "0.8rem", marginTop: 8, display: "flex", gap: 8 }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ flexShrink: 0, marginTop: 1 }}></i>
                <span>
                  Masa {selectedTable.tableNumber} minimum <b>{selectedTable.minCapacity} kişiliktir</b>, bu rezervasyon {reservation.partySize} kişi.
                  Yine de onaylarsanız rezervasyon bu masaya atanır.
                </span>
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.82rem" }}>
              <i className="bi bi-exclamation-circle me-1"></i>{error}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ padding: "9px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
            Vazgeç
          </button>
          <button onClick={handleConfirm} disabled={saving || !tableId}
            style={{
              padding: "9px 22px", borderRadius: 10, border: "none",
              background: saving || !tableId ? "#e5e7eb" : "linear-gradient(135deg, #0d9488, #0f766e)",
              color: saving || !tableId ? "#9ca3af" : "#fff", fontWeight: 700, fontSize: "0.85rem",
              cursor: saving || !tableId ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm"></span> Onaylanıyor...</>
              : <><i className="bi bi-check-lg"></i> Onayla</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
